import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarCheck2, CalendarPlus, Video, Radio, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useClassCountdown } from './Countdown';
import { CalendarLinks } from './ClassHeroCard';
import { goPro } from '../../lib/checkout';
import { classWhen, fill, joinHref, saveSeat } from '../../lib/nextSteps';

/** Slim reminder once the class is no longer the hero: title + when + countdown on the first line, the actions underneath. */
export default function ClassReminderStrip({ cls, texts, vars, onChanged }) {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const ev = cls.event;
  const c = useClassCountdown(ev.start_at);
  const t = texts.class_card;
  const href = joinHref(cls);
  const join = () => (!href ? navigate('/group-classes') : href.startsWith('/') ? navigate(href) : window.open(href, '_blank', 'noopener,noreferrer'));
  const save = async () => {
    setSaving(true);
    try { await saveSeat(cls); toast.success('Seat saved — calendar file downloaded'); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not save your seat'); }
    setSaving(false);
  };
  const tone = cls.live ? 'bg-rose-50 border-rose-200' : cls.seat_saved ? 'bg-emerald-50/70 border-emerald-200' : 'bg-white border-slate-200';
  return (
    <div className={`rounded-2xl border p-3 sm:px-4 sm:py-3 ${tone}`} data-testid="ns-reminder-strip" data-mode={cls.live ? 'live' : cls.seat_saved ? 'saved' : 'save'}>
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${cls.seat_saved ? 'bg-emerald-600 text-white' : 'bg-slate-900 text-white'}`}>
          {cls.live ? <Radio size={16} className="animate-pulse" /> : cls.seat_saved ? <CalendarCheck2 size={16} /> : <CalendarPlus size={16} />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 truncate">{cls.seat_saved ? t.saved_label : fill(t.heading, vars)}</p>
          <p className="text-sm font-semibold text-slate-900 leading-snug" data-testid="ns-strip-title">
            <span className="block sm:inline truncate">{ev.title}</span>
            <span className="block sm:inline text-slate-500 sm:text-slate-400 font-normal text-xs sm:text-sm"><span className="hidden sm:inline"> · </span>{classWhen(ev.start_at)}</span>
          </p>
        </div>
        {!cls.live && c && <span className="shrink-0 text-[11px] sm:text-xs font-bold tabular-nums text-slate-700 bg-white border border-slate-200 rounded-full px-2.5 sm:px-3 py-1" data-testid="ns-strip-countdown">{c.expired ? 'Starting now' : <><span className="hidden sm:inline">Starts </span>in {c.short}</>}</span>}
        {cls.live && <span className="shrink-0 text-xs font-bold text-rose-700" data-testid="ns-strip-live">{t.live_label}</span>}
      </div>
      <div className="mt-2.5 flex items-center gap-x-3 gap-y-2 flex-wrap pl-12 sm:pl-0">
        {cls.is_locked ? (
          <button type="button" onClick={() => goPro(navigate)} className="inline-flex items-center gap-1.5 rounded-full bg-amber-500 hover:bg-amber-400 text-slate-950 px-4 py-2 text-xs font-extrabold transition" data-testid="ns-strip-upgrade-btn">
            <Lock size={13} /> Upgrade to join
          </button>
        ) : cls.seat_saved ? (
          <>
            <button type="button" onClick={join} disabled={!cls.join_open} title={cls.join_open ? '' : t.join_soon_label}
              className={`items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold transition ${cls.join_open ? 'inline-flex bg-emerald-600 hover:bg-emerald-700 text-white' : 'hidden sm:inline-flex bg-slate-100 text-slate-400 cursor-not-allowed'}`} data-testid="ns-strip-join-btn" data-open={cls.join_open}>
              <Video size={13} /> {cls.join_open ? t.join_label : t.join_soon_label}
            </button>
            <CalendarLinks cls={cls} dark={false} label={t.calendar_label} />
          </>
        ) : (
          <button type="button" onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 rounded-full bg-amber-500 hover:bg-amber-400 text-slate-950 px-4 py-2 text-xs font-extrabold transition disabled:opacity-60" data-testid="ns-strip-save-btn">
            <CalendarPlus size={13} /> {saving ? 'Saving…' : 'Save my seat'}
          </button>
        )}
      </div>
    </div>
  );
}
